/**
 * Which identity providers this deployment can actually sign a visitor in with.
 *
 * <h3>Why this is baked in rather than fetched</h3>
 * <p>Before an identity is proved, the public chat route is the bundle and nothing else (D-37) —
 * there is a test asserting zero network calls. So the page cannot ask the server which providers
 * exist; the answer has to be in the bundle already.</p>
 *
 * <h3>Why it is not a hand-maintained flag</h3>
 * <p>It is derived at build time from the same CDK context that configures the backend, so a
 * provider appears here exactly when its client id is set there (D-85). A separate switch would be
 * a third source of truth after the context and the task definition, and the failure it produces is
 * the one this file exists to fix: a button that goes straight to a 404.</p>
 *
 * <p>Unset means <b>none</b>, deliberately. The wrong default is "all three": that is the state
 * that shipped, where a deployment with no marketplace still offered three dead buttons.</p>
 */
export interface GuestProvider {
  id: string;
  label: string;
}

const KNOWN: GuestProvider[] = [
  { id: "linkedin", label: "LinkedIn" },
  { id: "microsoft", label: "Microsoft" },
  { id: "google", label: "Google" },
];

export function guestProviders(
  configured: string | undefined = import.meta.env.VITE_A2A_PROVIDERS as string | undefined,
): GuestProvider[] {
  const wanted = new Set(
    (configured ?? "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  );
  // Intersected with the known list rather than trusted: a typo in the build config becomes a
  // missing button, never a link to a path that does not exist.
  return KNOWN.filter((p) => wanted.has(p.id));
}

/** Whether there is any way in at all. Nothing to offer means the page should not be offered. */
export function marketplaceIsOffered(
  configured: string | undefined = import.meta.env.VITE_A2A_PROVIDERS as string | undefined,
): boolean {
  return guestProviders(configured).length > 0;
}
